import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import type { User } from '../types/api';

interface AuthState {
  token: string | null;
  user: User | null;
}

interface AuthContextValue extends AuthState {
  login: (token: string, user: User) => void;
  logout: () => void;
  patchUser: (patch: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStorage(): AuthState {
  try {
    const raw = localStorage.getItem('pm_auth');
    if (!raw) return { token: null, user: null };
    const parsed = JSON.parse(raw) as { token?: string; user?: User };
    return {
      token: parsed.token ?? null,
      user: parsed.user ?? null,
    };
  } catch {
    return { token: null, user: null };
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(readStorage);

  useEffect(() => {
    // Sync state from localStorage on mount
    setState(readStorage());

    // Listen for changes from legacy auth.js (dispatches 'pm-auth-changed')
    // and from other tabs (fires 'storage' event)
    const sync = () => setState(readStorage());
    window.addEventListener('storage', sync);
    window.addEventListener('pm-auth-changed', sync);

    // Bridge: legacy Login.js calls window.onLoginSuccess after saving auth
    (window as any).onLoginSuccess = (token: string, user: User) => {
      setState({ token, user });
    };
    (window as any).onLogout = () => {
      setState({ token: null, user: null });
    };

    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('pm-auth-changed', sync);
      delete (window as any).onLoginSuccess;
      delete (window as any).onLogout;
    };
  }, []);

  const login = useCallback((token: string, user: User) => {
    const next = { token, user };
    localStorage.setItem('pm_auth', JSON.stringify(next));
    setState(next);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('pm_auth');
    setState({ token: null, user: null });
    window.location.href = '/login';
  }, []);

  const patchUser = useCallback((patch: Partial<User>) => {
    setState((prev) => {
      if (!prev.user) return prev;
      const updated = { ...prev.user, ...patch };
      const next = { ...prev, user: updated };
      localStorage.setItem('pm_auth', JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, patchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
